import axios from "axios";
import type {
  DrugCandidate,
  CandidateDetail,
  Company,
  PipelineAlert,
  DashboardSummary,
  PaginatedResponse,
  Contact,
  AuthUser,
} from "../types";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1",
});

export { api };

// Attach Clerk session token to every request
api.interceptors.request.use(async (config) => {
  const getToken = (window as any).__clerkGetToken;
  if (getToken) {
    try {
      const token = await getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // Not signed in — let the request go without auth
    }
  }
  return config;
});

// Notify AuthContext to refresh token count after POST requests
// (sync triggers, AI insights, etc. deduct tokens)
api.interceptors.response.use((response) => {
  if (response.config.method === "post") {
    window.dispatchEvent(new Event("tokens-changed"));
  }
  return response;
});

// ─── Pipeline ───────────────────────────────────────────────────────

export interface PipelineFilters {
  phase?: string;
  therapeutic_area?: string;
  sponsor?: string;
  min_probability?: number;
  status?: string;  // comma-separated for multi-select
  route?: string;   // comma-separated for multi-select
  company_tier?: string;
  org_type?: string;
  has_analysis?: boolean;
  show_approved?: boolean;
  sort_by?: string;
  sort_dir?: string;
  page?: number;
  page_size?: number;
}

export async function searchPipeline(
  filters: PipelineFilters = {}
): Promise<PaginatedResponse<DrugCandidate>> {
  const { data } = await api.get("/pipeline", { params: filters });
  return data;
}

export async function getCandidateDetail(
  id: number
): Promise<CandidateDetail> {
  const { data } = await api.get(`/pipeline/${id}`);
  return data;
}

export interface FilterOptions {
  phases: string[];
  therapeutic_areas: string[];
  statuses: string[];
  sponsors: string[];
  routes: string[];
  company_tiers: string[];
}

export async function getFilterOptions(): Promise<FilterOptions> {
  const { data } = await api.get("/pipeline/filter-options");
  return data;
}

// ─── Companies & Contacts ───────────────────────────────────────────

export async function listCompanies(params?: {
  search?: string;
  company_type?: string;
  company_tier?: string;
  sort_by?: string;
  sort_dir?: string;
  page?: number;
  page_size?: number;
}): Promise<PaginatedResponse<Company>> {
  const { data } = await api.get("/companies", { params });
  return data;
}

export async function getCompanyContacts(
  companyId: number
): Promise<{ company: string; contacts: Contact[] }> {
  const { data } = await api.get(`/companies/${companyId}/contacts`);
  return data;
}

export async function addContact(
  companyId: number,
  contact: {
    first_name: string;
    last_name: string;
    title?: string;
    email?: string;
    phone?: string;
    linkedin_url?: string;
    role_category?: string;
    notes?: string;
  }
): Promise<{ id: number; message: string }> {
  const { data } = await api.post(`/companies/${companyId}/contacts`, null, {
    params: contact,
  });
  return data;
}

// ─── Alerts ─────────────────────────────────────────────────────────

export async function getAlerts(params?: {
  unread_only?: boolean;
  alert_type?: string;
  page?: number;
}): Promise<PaginatedResponse<PipelineAlert>> {
  const { data } = await api.get("/alerts", { params });
  return data;
}

export async function markAlertRead(
  alertId: number
): Promise<{ message: string }> {
  const { data } = await api.post(`/alerts/${alertId}/read`);
  return data;
}

// ─── Dashboard ──────────────────────────────────────────────────────

export async function getDashboardSummary(): Promise<DashboardSummary> {
  const { data } = await api.get("/dashboard/summary");
  return data;
}

// ─── Export ─────────────────────────────────────────────────────────

export function getExportUrl(params?: {
  phase?: string;
  therapeutic_area?: string;
  format?: string;
}): string {
  const base =
    process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
  const query = new URLSearchParams();
  if (params?.phase) query.set("phase", params.phase);
  if (params?.therapeutic_area)
    query.set("therapeutic_area", params.therapeutic_area);
  query.set("format", params?.format || "csv");
  return `${base}/export/pipeline?${query.toString()}`;
}

// ─── Sync ───────────────────────────────────────────────────────────

export async function triggerSync(
  type: "clinical-trials" | "pricing" | "fda" | "who-trials" | "ema" | "patents" | "news" | "fda-approvals" | "approval-crossref" | "device-enrichment" | "trial-history" | "validate-pipeline" | "contacts" | "google-contacts" | "press-releases" | "dosing-enrichment" | "regulatory-milestones" | "company-enrichment" | "rebuild-programs" | "ml-scoring" | "pubmed" | "press-release-backfill" | "test-alignment" | "trial-field-backfill"
): Promise<Record<string, unknown>> {
  const { data } = await api.post(`/sync/${type}`);
  return data;
}

// ─── Drug Events ────────────────────────────────────────────────────

export async function getCandidateEvents(
  candidateId: number
): Promise<Record<string, unknown>> {
  const { data } = await api.get(`/pipeline/${candidateId}/events`);
  return data;
}

export async function searchDrugPressReleases(
  candidateId: number
): Promise<Record<string, unknown>> {
  const { data } = await api.post(`/pipeline/${candidateId}/search-press-releases`);
  return data;
}

// ─── Drug-Specific Contacts ─────────────────────────────────────────

export async function getContactsForDrug(
  candidateId: number
): Promise<Record<string, unknown>> {
  const { data } = await api.get(`/contacts/for-drug/${candidateId}`);
  return data;
}

export async function pdlFindForDrug(
  candidateId: number,
  roles?: string[]
): Promise<Record<string, unknown>> {
  const params: Record<string, string> = {};
  if (roles && roles.length) params.roles = roles.join(",");
  const { data } = await api.post(`/pdl/find-for-drug/${candidateId}`, null, { params });
  return data;
}

// ─── Trial History ──────────────────────────────────────────────────

export async function fetchTrialHistory(
  candidateId: number
): Promise<Record<string, unknown>> {
  const { data } = await api.post(`/pipeline/${candidateId}/fetch-history`);
  return data;
}

// ─── AI Insights ────────────────────────────────────────────────────

// ─── Probability Explanation ────────────────────────────────────────

export interface ProbabilityFactor {
  feature: string;
  label: string;
  impact: number; // percentage points, e.g. +12.5 or -8.3
}

export interface ProbabilityExplanation {
  available: boolean;
  probability?: number;
  base_rate?: number;
  positive_factors?: ProbabilityFactor[];
  negative_factors?: ProbabilityFactor[];
  reason?: string;
}

export async function getProbabilityExplanation(
  candidateId: number
): Promise<ProbabilityExplanation> {
  const { data } = await api.get(`/pipeline/${candidateId}/probability-explain`);
  return data;
}

// ─── Probability Timeline ───────────────────────────────────────────

export interface TimelinePoint {
  date: string;
  probability: number | null;
  phase: string;
  event: string;
  trial_title: string | null;
  nct_id: string | null;
  enrollment: number | null;
  n_trials_known: number | null;
  indication: string | null;
}

export interface ProbabilityTimeline {
  drug_name: string;
  therapeutic_area: string;
  current_probability: number | null;
  timeline: TimelinePoint[];
}

export async function getProbabilityTimeline(
  candidateId: number,
  indication?: string
): Promise<ProbabilityTimeline> {
  const params = indication ? { indication } : {};
  const { data } = await api.get(`/pipeline/${candidateId}/probability-timeline`, { params });
  return data;
}

export async function getCandidateInsights(
  candidateId: number
): Promise<Record<string, unknown>> {
  const { data } = await api.get(`/pipeline/${candidateId}/insights`);
  return data;
}

export async function generateCandidateInsights(
  candidateId: number
): Promise<Record<string, unknown>> {
  const { data } = await api.post(`/pipeline/${candidateId}/insights`);
  return data;
}

// ─── Contact Search ──────────────────────────────────────────────────

export interface ContactSearchParams {
  name?: string;
  company?: string;
  drug?: string;
  role?: string;
  source?: string;
  has_email?: boolean;
  page?: number;
  page_size?: number;
}

export async function searchContacts(
  params: ContactSearchParams = {}
): Promise<PaginatedResponse<Contact>> {
  const { data } = await api.get("/contacts/search", { params });
  return data;
}

export async function getEnrichmentStats(): Promise<Record<string, unknown>> {
  const { data } = await api.get("/contacts/enrichment-stats");
  return data;
}

// ─── Auth ──────────────────────────────────────���────────────────────

export async function getMe(): Promise<AuthUser> {
  const { data } = await api.get("/auth/me");
  return data;
}

// ─── Admin ──────────────────────────────────────────────────────────

export async function listUsers(): Promise<{ users: (AuthUser & { is_active: boolean; created_at: string | null; last_login: string | null })[] }> {
  const { data } = await api.get("/admin/users");
  return data;
}

export async function updateUser(
  userId: number,
  updates: Partial<{ token_limit: number; is_active: boolean; is_admin: boolean; tokens_used: number }>
): Promise<{ message: string }> {
  const { data } = await api.patch(`/admin/users/${userId}`, updates);
  return data;
}

// ─── Sync Status (authenticated) ────────────────────────────────────

export async function getSyncTaskStatus(taskId: string): Promise<Record<string, unknown>> {
  const { data } = await api.get(`/sync/status/${taskId}`);
  return data;
}

export async function getSyncStatus(): Promise<Record<string, unknown>> {
  const { data } = await api.get("/sync/status");
  return data;
}
